import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DollarSign, TrendingUp, CreditCard, Loader2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";

const RevenueDashboard = () => {
  const { language } = useLanguage();
  const bn = language === "bn";
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from("payments").select("*").order("created_at", { ascending: false });
      setPayments(data ?? []);
      setLoading(false);
    };
    load();
  }, []);

  const totalRevenue = payments.filter(p => p.status === "paid" || p.status === "completed").reduce((s, p) => s + (p.amount || 0), 0);
  const totalPayments = payments.length;

  const stats = [
    { icon: DollarSign, labelEn: "Total Revenue", labelBn: "মোট রাজস্ব", value: `৳${totalRevenue.toLocaleString()}` },
    { icon: CreditCard, labelEn: "Total Payments", labelBn: "মোট পেমেন্ট", value: totalPayments },
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="grid grid-cols-2 gap-4">
        {stats.map((s, i) => (
          <Card key={i} className="bg-card/60 backdrop-blur-sm border-border/40">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center"><s.icon className="h-5 w-5 text-primary" /></div>
                {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
              </div>
              <p className="text-2xl font-extrabold tracking-tight">{typeof s.value === "number" ? s.value.toLocaleString() : s.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{bn ? s.labelBn : s.labelEn}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-card/60 backdrop-blur-sm border-border/40">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-bold text-foreground/80 uppercase tracking-wider">{bn ? "সাম্প্রতিক লেনদেন" : "Recent Transactions"}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {payments.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-12">{bn ? "কোন লেনদেন নেই" : "No transactions yet"}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border/40">
                  <TableHead>{bn ? "তারিখ" : "Date"}</TableHead>
                  <TableHead>{bn ? "মেথড" : "Method"}</TableHead>
                  <TableHead>{bn ? "পরিমাণ" : "Amount"}</TableHead>
                  <TableHead>{bn ? "স্থিতি" : "Status"}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.slice(0, 20).map((t) => (
                  <TableRow key={t.id} className="border-border/30 hover:bg-muted/20">
                    <TableCell className="text-sm">{new Date(t.created_at).toLocaleDateString()}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{t.method ?? "—"}</Badge></TableCell>
                    <TableCell className="font-bold text-sm">৳{(t.amount ?? 0).toLocaleString()}</TableCell>
                    <TableCell>
                <Badge variant={t.status === "paid" || t.status === "completed" ? "default" : "secondary"} className="text-[10px] capitalize">{t.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RevenueDashboard;
